import {
  createPrintError,
  createPrintResult,
  PRINT_ERROR_CODES,
} from "../contracts";

const DEFAULT_CLEANTER_URL = "http://localhost:9100";
const DEFAULT_TIMEOUT_MS = 30000;
const MAX_PRINT_CONTENT_BYTES = 1024 * 1024;

function getCleanterUrl() {
  return DEFAULT_CLEANTER_URL;
}

function getPaperCharacters(printer = {}) {
  return String(printer?.paper_width) === "58" ? 32 : 48;
}

function truncateText(value, width) {
  const text = String(value ?? "");
  return text.length > width ? text.slice(0, width) : text;
}

function formatMoney(value) {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return Math.round(number).toLocaleString("id-ID");
}

function createDivider(width) {
  return "-".repeat(width);
}

function createRow(left, right, width) {
  const leftText = String(left ?? "");
  const rightText = String(right ?? "");

  const available = Math.max(1, width - rightText.length - 1);
  const safeLeft = truncateText(leftText, available);

  return {
    type: "row",
    left: safeLeft,
    right: rightText,
  };
}

function normalizeItems(order = {}) {
  if (Array.isArray(order?.items)) {
    return order.items;
  }

  if (Array.isArray(order?.order_items)) {
    return order.order_items;
  }

  return [];
}

function buildReceiptContent(order = {}, printer = {}) {
  const width = getPaperCharacters(printer);
  const content = [];

  const outletName =
    order?.outlet_name ||
    order?.outlet?.name ||
    order?.store_name ||
    "GLOO POS";

  const outletAddress =
    order?.outlet_address ||
    order?.outlet?.address ||
    "";

  const transactionNumber =
    order?.transaction_number ||
    order?.transactionNumber ||
    "";

  const customerName =
    order?.customer_name ||
    order?.customerName ||
    "";

  const cashier =
    order?.cashier ||
    order?.cashier_name ||
    order?.created_by_name ||
    "";

  content.push({
    type: "text",
    text: truncateText(outletName, width),
    align: "center",
    bold: true,
  });

  if (outletAddress) {
    content.push({
      type: "text",
      text: truncateText(outletAddress, width),
      align: "center",
    });
  }

  content.push({ type: "divider" });

  if (transactionNumber) {
    content.push({
      type: "text",
      text: `No: ${truncateText(transactionNumber, Math.max(1, width - 4))}`,
    });
  }

  if (customerName) {
    content.push({
      type: "text",
      text: `Customer: ${truncateText(customerName, Math.max(1, width - 10))}`,
    });
  }

  if (cashier) {
    content.push({
      type: "text",
      text: `Cashier: ${truncateText(cashier, Math.max(1, width - 9))}`,
    });
  }

  content.push({ type: "divider" });

  const items = normalizeItems(order);

  items.forEach((item) => {
    const name =
      item?.name ||
      item?.product_name ||
      item?.menu_name ||
      "Item";

    const quantity = Number(item?.qty ?? item?.quantity ?? 1);
    const lineTotal = Number(
      item?.line_total ??
        item?.total ??
        item?.subtotal ??
        item?.price ??
        0
    );

    content.push(
      createRow(
        `${quantity}x ${name}`,
        formatMoney(lineTotal),
        width
      )
    );

    if (item?.notes) {
      content.push({
        type: "text",
        text: `  ${truncateText(item.notes, Math.max(1, width - 2))}`,
      });
    }
  });

  content.push({ type: "divider" });

  const subtotal = Number(order?.subtotal || 0);
  const discount = Number(order?.discount?.amount ?? order?.discount ?? 0);
  const tax = Number(order?.tax?.amount ?? order?.tax ?? 0);
  const serviceCharge = Number(order?.service_charge?.amount ?? order?.service_charge ?? 0);
  const grandTotal = Number(
    order?.grand_total ??
      order?.total ??
      order?.total_amount ??
      0
  );

  if (subtotal) {
    content.push(createRow("Subtotal", formatMoney(subtotal), width));
  }

  if (discount) {
    content.push(
      createRow("Discount", formatMoney(discount), width)
    );
  }

  if (tax) {
    content.push(createRow("Tax", formatMoney(tax), width));
  }

  if (serviceCharge) {
    content.push(
      createRow("Service", formatMoney(serviceCharge), width)
    );
  }

  content.push(
    createRow("TOTAL", formatMoney(grandTotal), width)
  );

  const paymentMethod =
    order?.payment_method ||
    order?.payment?.method ||
    "";

  const paymentAmount =
    order?.payment_amount ??
    order?.payment?.amount ??
    0;

  const change =
    order?.change ??
    order?.payment?.change ??
    0;

  if (paymentMethod) {
    content.push(
      createRow("Payment", paymentMethod, width)
    );
  }

  if (paymentAmount) {
    content.push(
      createRow("Paid", formatMoney(paymentAmount), width)
    );
  }

  if (change) {
    content.push(
      createRow("Change", formatMoney(change), width)
    );
  }

  const footer =
    order?.receipt_footer ||
    order?.footer ||
    "";

  if (footer) {
    content.push({ type: "divider" });
    content.push({
      type: "text",
      text: truncateText(footer, width),
      align: "center",
    });
  }

  content.push({ type: "feed", lines: 3 });

  return content;
}

function buildOpenBillContent(order = {}, printer = {}) {
  const width = getPaperCharacters(printer);
  const content = [];
  const outletName =
    order?.outlet_name || order?.outlet?.name || order?.store_name || "GLOO POS";
  const outletAddress = order?.outlet_address || order?.outlet?.address || "";
  const batches =
    Array.isArray(order?.order_batches) && order.order_batches.length
      ? order.order_batches
      : [{
          sequence: 1,
          created_at: order?.created_at,
          items: normalizeItems(order),
          subtotal: order?.subtotal || 0,
          discount: order?.discount?.amount ?? order?.discount ?? 0,
          tax: order?.tax || 0,
          service_charge: order?.service_charge || 0,
          grand_total: order?.grand_total || 0,
        }];

  const addText = (text, options = {}) => {
    content.push({
      type: "text",
      text: truncateText(text, width),
      ...options,
    });
  };
  const addAmount = (label, value) => {
    const amount = Number(value ?? 0);
    if (Number.isFinite(amount) && amount > 0) {
      content.push(createRow(label, formatMoney(amount), width));
    }
  };

  addText(outletName, { align: "center", bold: true });
  if (outletAddress) addText(outletAddress, { align: "center" });
  content.push({ type: "divider" });
  addText("OPEN BILL / INVOICE", { align: "center", bold: true });
  addText("OUTSTANDING / BELUM DIBAYAR", { align: "center", bold: true });

  if (order?.transaction_number) addText(`No: ${order.transaction_number}`);
  if (order?.customer_name) addText(`Customer: ${order.customer_name}`);
  if (order?.created_at) addText(`Opening: ${order.created_at}`);
  if (order?.updated_at) addText(`Last Update: ${order.updated_at}`);

  const openedBy = order?.opened_by?.name || order?.cashier?.name ||
    order?.cashier || order?.cashier_name || "";
  if (openedBy) addText(`Opened by: ${openedBy}`);

  batches.forEach((batch, index) => {
    content.push({ type: "divider" });
    addText(`DAY / ORDER #${batch?.sequence || index + 1}`, { bold: true });
    if (batch?.created_at) addText(String(batch.created_at));

    const items = Array.isArray(batch?.items) ? batch.items : [];
    items.forEach((item) => {
      const name = item?.name || item?.product_name || item?.menu_name || "Item";
      const quantity = Number(item?.qty ?? item?.quantity ?? 1);
      const lineTotal = Number(
        item?.line_total ?? item?.total ?? item?.subtotal ?? item?.price ?? 0
      );
      content.push(createRow(`${quantity}x ${name}`, formatMoney(lineTotal), width));
      if (item?.notes) addText(`  ${item.notes}`);
    });

    const discount = Number(batch?.discount?.amount ?? batch?.discount ?? 0);
    const tax = Number(batch?.tax?.amount ?? batch?.tax ?? 0);
    const service = Number(
      batch?.service_charge?.amount ?? batch?.service_charge ?? 0
    );

    addAmount("Subtotal", batch?.subtotal);
    if (Number.isFinite(discount) && discount > 0) {
      content.push(createRow("Discount", `-${formatMoney(discount)}`, width));
    }
    addAmount(batch?.tax?.name || "Tax", tax);
    addAmount(batch?.service_charge?.name || "Service", service);
    content.push(createRow("DAY TOTAL", formatMoney(batch?.grand_total), width));
  });

  content.push({ type: "divider" });
  content.push(createRow("GRAND TOTAL", formatMoney(order?.grand_total), width));
  content.push(createRow("OUTSTANDING", formatMoney(order?.grand_total), width));
  content.push({ type: "feed", lines: 3 });
  return content;
}

function buildTestContent(printer = {}) {
  const width = getPaperCharacters(printer);

  return [
    {
      type: "text",
      text: "GLOO POS",
      align: "center",
      bold: true,
      size: "large",
    },
    {
      type: "text",
      text: "TEST PRINT",
      align: "center",
    },
    {
      type: "divider",
    },
    {
      type: "row",
      left: "Connection",
      right: "Cleanter",
    },
    {
      type: "row",
      left: "Printer",
      right: truncateText(printer?.name || "-", 20),
    },
    {
      type: "row",
      left: "Paper",
      right: `${String(printer?.paper_width || "80")}mm`,
    },
    {
      type: "divider",
    },
    {
      type: "text",
      text: `Width: ${width} chars`,
      align: "center",
    },
    {
      type: "feed",
      lines: 3,
    },
  ];
}


function formatKitchenExtra(value) {
  if (value == null || value === "") return "";

  if (Array.isArray(value)) {
    return value.map(formatKitchenExtra).filter(Boolean).join(", ");
  }

  if (typeof value === "object") {
    return String(
      value.name ||
      value.label ||
      value.option_name ||
      value.value ||
      value.title ||
      ""
    );
  }

  return String(value);
}

function wrapKitchenText(value, width) {
  const words = String(value ?? "").trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";

  for (const word of words) {
    if (!line) {
      if (word.length <= width) {
        line = word;
      } else {
        for (let i = 0; i < word.length; i += width) {
          lines.push(word.slice(i, i + width));
        }
      }
      continue;
    }

    if ((line + " " + word).length <= width) {
      line += " " + word;
    } else {
      lines.push(line);
      if (word.length <= width) {
        line = word;
      } else {
        line = "";
        for (let i = 0; i < word.length; i += width) {
          lines.push(word.slice(i, i + width));
        }
      }
    }
  }

  if (line) lines.push(line);
  return lines;
}

function buildKitchenTicketContent(ticket = {}, printer = {}) {
  const width = getPaperCharacters(printer);
  const content = [];
  const addText = (text, options = {}) => {
    for (const line of wrapKitchenText(text, width)) {
      content.push({ type: "text", text: line, ...options });
    }
  };

  addText("*** TIKET DAPUR ***", { align: "center", bold: true, size: "large" });
  content.push({ type: "divider" });

  if (ticket.transaction_number) {
    addText(`Transaksi: ${ticket.transaction_number}`, { bold: true });
  }

  if (ticket.batch_sequence != null) {
    addText(`Batch: ${ticket.batch_sequence}`, { bold: true });
  }

  if (ticket.customer_name) {
    addText(`Pelanggan: ${ticket.customer_name}`);
  }

  if (ticket.created_at) {
    const date = new Date(ticket.created_at);
    if (!Number.isNaN(date.getTime())) {
      addText(
        "Waktu: " + date.toLocaleString("id-ID", {
          timeZone: "Asia/Jakarta",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }) + " WIB"
      );
    }
  }

  content.push({ type: "divider" });

  const items = Array.isArray(ticket.items) ? ticket.items : [];
  for (const item of items) {
    const qty = Number(item?.qty ?? 0);
    addText(`${qty}x ${item?.name || "Menu tanpa nama"}`, { bold: true });

    const variants = formatKitchenExtra(item?.variants);
    const modifiers = formatKitchenExtra(item?.modifiers);

    if (variants) addText(`  Varian: ${variants}`);
    if (modifiers) addText(`  Tambahan: ${modifiers}`);

    if (item?.notes) {
      addText(`  CATATAN: ${item.notes}`, { bold: true });
    }

    content.push({ type: "divider" });
  }

  content.push({ type: "feed", lines: 3 });
  return content;
}

function buildCleanterDocument(job) {
  if (job?.document_type === "test") {
    return {
      cut: Boolean(job?.printer?.capabilities?.cut),
      content: buildTestContent(job.printer),
    };
  }

  if (job?.document_type === "open_bill") {
    return {
      cut: Boolean(job?.printer?.capabilities?.cut),
      content: buildOpenBillContent(job?.content || {}, job?.printer || {}),
    };
  }

  if (job?.document_type === "kitchen_ticket") {
    return {
      cut: Boolean(job?.printer?.capabilities?.cut),
      content: buildKitchenTicketContent(
        job?.content || {},
        job?.printer || {}
      ),
    };
  }

  return {
    cut: Boolean(job?.printer?.capabilities?.cut),
    content: buildReceiptContent(
      job?.content || {},
      job?.printer || {}
    ),
  };
}

async function cleanterRequest(
  path,
  {
    method = "GET",
    body = null,
    job = null,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  } = {}
) {
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    timeoutMs
  );

  try {
    const response = await fetch(
      `${getCleanterUrl()}${path}`,
      {
        method,
        headers: {
          Accept: "application/json",
          ...(body !== null
            ? { "Content-Type": "application/json" }
            : {}),
        },
        body:
          body === null
            ? undefined
            : JSON.stringify(body),
        signal: controller.signal,
      }
    );

    const contentType =
      response.headers.get("content-type") || "";

    let payload = null;

    if (contentType.includes("application/json")) {
      payload = await response.json();
    } else {
      const text = await response.text();
      payload = text ? { message: text } : null;
    }

    if (!response.ok) {
      const error = new Error(
        payload?.message ||
          payload?.error ||
          `Cleanter HTTP ${response.status}`
      );

      error.http_status = response.status;
      error.cleanter_payload = payload;

      if (response.status === 404) {
        error.code = PRINT_ERROR_CODES.ADAPTER_UNAVAILABLE;
      } else if (response.status === 408) {
        error.code = PRINT_ERROR_CODES.TIMEOUT;
      } else if (response.status === 503) {
        error.code = PRINT_ERROR_CODES.CONNECTION_FAILED;
      } else {
        error.code =
          payload?.code ||
          PRINT_ERROR_CODES.WRITE_FAILED;
      }

      throw error;
    }

    return payload;
  } catch (error) {
    if (error?.name === "AbortError") {
      throw createPrintError(
        PRINT_ERROR_CODES.TIMEOUT,
        "Cleanter tidak merespons dalam batas waktu.",
        {
          job,
          adapter: "cleanter",
          cause: error,
        }
      );
    }

    if (error?.code) {
      throw error;
    }

    throw createPrintError(
      PRINT_ERROR_CODES.BRIDGE_NOT_RUNNING,
      "Cleanter tidak berjalan di perangkat Android.",
      {
        job,
        adapter: "cleanter",
        cause: error,
      }
    );
  } finally {
    clearTimeout(timeout);
  }
}

export async function checkCleanter() {
  return cleanterRequest("/health");
}

export async function printCleanterAdapter({ job }) {
  if (!job?.printer) {
    throw createPrintError(
      PRINT_ERROR_CODES.PRINTER_NOT_CONFIGURED,
      "Profil printer Cleanter belum tersedia.",
      {
        job,
        adapter: "cleanter",
      }
    );
  }

  const document = buildCleanterDocument(job);
  const serialized = JSON.stringify(document);

  if (
    new TextEncoder().encode(serialized).byteLength >
    MAX_PRINT_CONTENT_BYTES
  ) {
    throw createPrintError(
      PRINT_ERROR_CODES.PRINT_JOB_REJECTED,
      "Ukuran print job Cleanter melebihi batas 1 MB.",
      {
        job,
        adapter: "cleanter",
      }
    );
  }

  try {
    const result = await cleanterRequest("/print", {
      method: "POST",
      job,
      body: {
        ...document,
      },
    });

    if (result?.success === false) {
      throw createPrintError(
        result?.code || PRINT_ERROR_CODES.WRITE_FAILED,
        result?.message || "Cleanter melaporkan print job gagal.",
        { job, adapter: "cleanter" }
      );
    }

    return createPrintResult({
      job,
      success: true,
      adapter: "cleanter",
      transport_result: result,
    });
  } catch (error) {
    if (error?.code) {
      error.job_id = error.job_id || job.job_id;
      error.adapter = error.adapter || "cleanter";
      throw error;
    }

    throw createPrintError(
      PRINT_ERROR_CODES.WRITE_FAILED,
      error?.message ||
        "Gagal mengirim print job ke Cleanter.",
      {
        job,
        adapter: "cleanter",
        cause: error,
      }
    );
  }
}

export {
  DEFAULT_CLEANTER_URL,
  DEFAULT_TIMEOUT_MS,
};
